import { useEffect, useContext } from "react";
import { useFormContext } from "react-hook-form";
import { classes } from "../../../../util/style-utils";
import PhoneNumber from "../../../gdk/PhoneNumber";
import RadioButtonGroup from "../../../gds/RadioButtonGroup";
import SuffixOptions from "../../constants/options/SuffixOptions";
import YesNoOptions from "../../../../constants/options/YesNoOptions";
import { ClientConfigurationContext } from "../../../../context/ClientConfigurationContextProvider";

export default function PersonalInformationFields() {
    const { register, formState: { errors }, clearErrors, control, watch, setValue } = useFormContext();
    const watchOffshoreResident = watch("offshoreResident");
    const isOffshoreResident = watchOffshoreResident === true || watchOffshoreResident === "true";

    const clientConfiguration = useContext(ClientConfigurationContext);

    const enableOffshore = clientConfiguration?.enableOffshore;

    useEffect(() => {
        if (!enableOffshore || !isOffshoreResident) {
            setValue("mobilePhoneCountryCode", "+1");
            setValue("businessPhoneCountryCode", "+1");
            return;
        }

        setValue("mobilePhoneCountryCode", watch("mobilePhoneCountryCode") || "+1");
        setValue("businessPhoneCountryCode", watch("businessPhoneCountryCode") || "+1");
    }, [isOffshoreResident, enableOffshore, setValue, watch]);

    return (
        <>
            <div className="flex-row label-row">
                <div className="form-field">
                    <label htmlFor="first-name" className="text">
                        First Name<span className="required">*</span>
                    </label>
                </div>
                <div className="form-field">
                    <label htmlFor="middle-name" className="text">
                        Middle Name
                    </label>
                    <span className='form-descriptive-copy'>Optional</span>
                </div>
            </div>

            <div className="flex-row">
                <div className={classes("form-field", errors.firstName && "form-field--error")}>
                    <input
                        id="first-name"
                        type="text"
                        placeholder="First Name"
                        maxLength={50}
                        {...register("firstName", { onBlur: () => { clearErrors("firstName"); } })}
                    />
                    {errors.firstName && (
                        <span className="form-message">
                            {errors.firstName.message}
                        </span>
                    )}
                </div>

                <div className={classes("form-field", errors.middleName && "form-field--error")}>
                    <input
                        id="middle-name"
                        type="text"
                        size="30"
                        maxLength={50}
                        placeholder="Middle Name"
                        {...register("middleName")}
                    />
                    {errors.middleName && (
                        <span className="form-message">
                            {errors.middleName.message}
                        </span>
                    )}
                </div>
            </div>

            <div className="flex-row label-row">
                <div className="form-field">
                    <label htmlFor="last-name" className="text">
                        Last Name<span className="required">*</span>
                    </label>
                </div>
                <div className="form-field">
                    <label htmlFor="suffix" className="text">
                        Suffix
                    </label>
                    <span className='form-descriptive-copy'>Optional</span>
                </div>
            </div>

            <div className="flex-row">
                <div className={classes("form-field", errors.lastName && "form-field--error")}>
                    <input
                        id="last-name"
                        type="text"
                        placeholder="Last Name"
                        maxLength={50}
                        {...register("lastName", { onBlur: () => { clearErrors("lastName"); } })}
                    />
                    {errors.lastName && (
                        <span className="form-message">
                            {errors.lastName.message}
                        </span>
                    )}
                </div>

                <div className={classes("form-field", errors.suffix && "form-field--error")}>
                    <div className="select-box">
                        <select id="suffix" {...register("suffix")}>
                            {SuffixOptions?.map(option => (
                                <option value={option.value} key={option.value}>{option.label}</option>
                            ))}
                        </select>
                    </div>
                    {errors.suffix &&
                        <span className="form-message">{errors.suffix.message}</span>
                    }
                </div>
            </div>

            {enableOffshore && <div className="flex-row">
                <div className="form-field offshore-resident-question">
                    <RadioButtonGroup trackingId="CreateYourProfile:PersonalInformationFields:OffshoreResident"
                        control={control}
                        name="offshoreResident"
                        legend={<>Do you reside outside of the U.S.?<span className="required">*</span></>}
                        options={YesNoOptions}
                        layout="Two Column"
                        defaultValue="false"
                    />
                </div>
            </div>
            }

            <PhoneNumber
                control={control}
                name="mobilePhone"
                label={<>Mobile Phone Number<span className="required">*</span></>}
                descriptiveText={<i>This phone number will be used to verify your identity before logging into our GEICO portal.</i>}
                showCountryCode={enableOffshore && isOffshoreResident}
                countryCodeName="mobilePhoneCountryCode"
                countryCodeLabel={<>Mobile Phone Country Code<span className="required">*</span></>}
            />

            <div className="flex-row">
                <fieldset className={classes("form-field checkbox-group", errors.textOptIn)}>
                    <div className="checkbox-wrapper">
                        <div>
                            <input data-onboarding-checkbox-id="CreateYourProfile:PersonalInformationFields:TextMessageOptIn" id="text-opt-in" type="checkbox" {...register("textOptIn")} />
                            <label htmlFor="text-opt-in" className="checkbox">
                                Receive text updates about GEICO For Your Agency
                            </label>
                        </div>
                    </div>
                </fieldset>
            </div>

            {enableOffshore && isOffshoreResident &&
                <div>
                    <PhoneNumber
                        control={control}
                        name="businessPhone"
                        label={<>Business Phone Number<span className="required">*</span></>}
                        descriptiveText="This phone number will be displayed on customer documents when you quote or sell a policy."
                        showCountryCode={enableOffshore && isOffshoreResident}
                        countryCodeName="businessPhoneCountryCode"
                        countryCodeLabel={<>Business Phone Country Code<span className="required">*</span></>}
                    />

                    <div className="flex-row">
                        <div className={classes("form-field", errors.businessEmail && "form-field--error")}>
                            <label htmlFor="business-email" className="text">
                                Business Email Address<span className="required">*</span>
                            </label>
                        </div>
                    </div>

                    <div className="flex-row">
                        <div className={classes("form-field", errors.businessEmail && "form-field--error")}>
                            <input
                                id="business-email"
                                type="email"
                                {...register("businessEmail")}
                            />
                            {errors.businessEmail &&
                                <span className="form-message">{errors.businessEmail.message}</span>
                            }
                        </div>
                    </div>
                </div>
            }

            {(!enableOffshore || !isOffshoreResident) &&
                <div>
                    <div className="flex-row label-row">
                        <div className={classes("form-field")}>
                            <label htmlFor="businessPhone-input" className="text">
                                Business Phone Number<span className="required">*</span>
                            </label>
                            <span className="form-descriptive-copy">
                                <i>This phone number will be displayed on customer documents when you quote or sell a policy.</i>
                            </span>
                        </div>
                        <div className={classes("form-field", errors.businessEmail && "form-field--error")}>
                            <label htmlFor="business-email" className="text">
                                Business Email Address<span className="required">*</span>
                            </label>
                        </div>
                    </div>

                    <div className="flex-row">
                        {/* The label for this phone number is in the label-row above for alignment reasons. */}
                        <PhoneNumber
                            control={control}
                            name="businessPhone"
                        />

                        <div className={classes("form-field", errors.businessEmail && "form-field--error")}>
                            <input
                                id="business-email"
                                type="email"
                                {...register("businessEmail")}
                            />
                            {errors.businessEmail &&
                                <span className="form-message">{errors.businessEmail.message}</span>
                            }
                        </div>
                    </div>
                </div>
            }
        </>
    );
}
